<script setup lang="ts">

import { useAssetStore } from '@/stores/assetStore.ts'
import { onMounted, reactive, watch } from 'vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import Backend from '@/models/backend.ts'
import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const assetStore = useAssetStore()
const planetStore = usePlanetStore()
const gameStore = useGameStore()
const backend = new Backend()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildings = reactive(new Map<number, any>());

const calculateList = () => {
  // Clear existing buildings to ensure deleted types are removed
  buildings.clear()
  // Iterate through all buildings and count them
  let buildingList = new Map<string, number>()
  for(const planet of planetStore.planets.values()) {
    for(const buildingCode of planet.buildings) {
      if(buildingList.has(buildingCode)) {
        const value = buildingList.get(buildingCode) ?? 0
        buildingList.set(buildingCode, value + 1)
      } else {
        buildingList.set(buildingCode, 1)
      }
    }
  }
  // For each building in the buildinList, check the assetStore for the effects and calculate the total cost
  let index = 0
  for(const [buildingCode, amount] of buildingList) {
    const building = assetStore.buildings.get(buildingCode)
    if(building) {
      let creditsPerBuilding = 0
      for (const [, value] of Object.entries(building.effects)) {
        if (value.type === 'credit') {
          creditsPerBuilding += value.bonus
        }
      }
      const totalCredits = creditsPerBuilding * amount
      if(totalCredits === 0) {
        continue
      }
      if(amount === 0) {
        continue
      }
      buildings.set(index++, {
        id: buildingCode,
        name: t(`buildings.buildings.${buildingCode}.name`),
        quantity: amount,
        creditsPerBuilding: creditsPerBuilding,
        totalCredits: totalCredits,
        effects: building.effects
      })
    }
  }
}

onMounted(() => {
  calculateList()
})

const loadBuilding = (buildingCode: string) => {
  gameStore.modalType = 'building'
  gameStore.modalData = {
    buildingCode: buildingCode
  }
}

const deleteBuilding = (buildingCode: string) => {
  // Find the planet with the building
  for(const planet of planetStore.planets.values()) {
    if(planet.buildings.includes(buildingCode)) {
      backend.deleteBuildingFromPlanet(planet.id, buildingCode).then(() => {
        gameStore.setTinySuccessToastMessage(t('notifications.success.buildingDeleted'))
      })
      return
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filters = (buildings: any[]) => {
  return buildings
}

watch(() => planetStore.planets, () => {
  calculateList()
}, { deep: true })

</script>

<template>
  <StandardCard top-padding="0" :title="t('government.finances.buildings.title')" :help="t('government.finances.buildings.help')"/>
  <div class="mt-1"></div>
  <FerionDataTable
    :data="buildings"
    tableStoreName="financeBuildings"
    :selector="false"
    :filters="filters"
    :heightLimit="'h-[calc(100vh-19.5rem)]'"
    :no-result-message="t('common.status.noDataYet')"
    :click="loadBuilding"
    :footer-bar="false"
  >
    <template #columns="{ sort, sortColumn, sortDirection }">
      <HeaderTextFlex min-width="10" column="name" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" class="rounded-tl">
        <div class="pl-2">{{ t('government.finances.buildings.columns.building') }}</div>
      </HeaderTextFlex>
      <HeaderFixedWidthText width="5" column="quantity" :sortDirection="sortDirection" :sort-column="sortColumn" @sort="sort" align="center">
        {{ t('government.finances.buildings.columns.quantity') }}
      </HeaderFixedWidthText>
      <HeaderFixedWidthText width="10" column="creditsPerBuilding" :sortDirection="sortDirection" :sort-column="sortColumn" @sort="sort" align="center">
        {{ t('government.finances.buildings.columns.creditsPerBuilding') }}
      </HeaderFixedWidthText>
      <HeaderFixedWidthText width="10" column="totalCredits" :sortDirection="sortDirection" :sort-column="sortColumn" @sort="sort" align="center">
        {{ t('government.finances.buildings.columns.totalCredits') }}
      </HeaderFixedWidthText>
      <HeaderFixedWidthText width="10" column="" :sortDirection="sortDirection" :sort-column="sortColumn" @sort="sort" align="center" class="rounded-tr">
        {{ t('government.finances.buildings.columns.action') }}
      </HeaderFixedWidthText>
    </template>
    <!--- Rows --->
    <template #rows="{ item }" >
      <CellFlexWidth min-width="10">
        <div class="flex-col ml-2 ">
          {{ item.name }}
        </div>
      </CellFlexWidth>
      <CellFixedWidth width="5">
        <div class="flex-col ml-2 ">
          {{ item.quantity }}
        </div>
      </CellFixedWidth>
      <CellFixedWidth width="10">
        <div class="flex-col ml-2 ">
          <GenericIconWithColoredNumber :amount="item.creditsPerBuilding" type="credit"/>
        </div>
      </CellFixedWidth>
      <CellFixedWidth width="10">
        <div class="flex-col ml-2 ">
          <GenericIconWithColoredNumber :amount="item.totalCredits" type="credit"/>
        </div>
      </CellFixedWidth>
      <CellFixedWidth width="10">
        <div class="flex-col ml-2 ">
          <DeleteButton @click.stop="deleteBuilding(item.id)">{{ t('planets.buildings.deleteOne') }}</DeleteButton>
        </div>
      </CellFixedWidth>
    </template>
  </FerionDataTable>
</template>

<style scoped lang="scss">

</style>